import { AutomatedReleaseInstructionKind } from '../automated-release/types';
import { ClauseInstructionKind } from '../clauses/constants';
import { ExpiringTargetsInstructionKind } from '../expiringTargets/types';
import { MembersAndTeamsInstructionKind } from '../membersAndTeams/types';
import { OnOffInstructionKind } from '../onOff/types';
import { FlagPrerequisitesInstructionKind } from '../prerequisites/types';
import { RuleInstructionKind } from '../rules/constants';
import { TargetsInstructionKind } from '../targets/types';
import { UserTargetsInstructionKind } from '../userTargets/types';
import { FlagVariationsInstructionKind } from '../variations/types';

export enum ScheduleInstructionKind {
  UPDATE_SCHEDULED_CHANGES_EXECUTION_DATE = 'updateScheduledChangesExecutionDate',
  REPLACE_SCHEDULED_CHANGES = 'replaceScheduledChangesInstructions',
  DELETE_SCHEDULED_CHANGES = 'deleteScheduledChange',
}

export enum InstructionCategory {
  USER_TARGETS = 'USER_TARGETS',
  REPLACE_USER_TARGETS = 'REPLACE_USER_TARGETS',
  TARGETS = 'TARGETS',
  REPLACE_TARGETS = 'REPLACE_TARGETS',
  PREREQUISITES = 'PREREQUISITES',
  REPLACE_PREREQUISITES = 'REPLACE_PREREQUISITES',
  ON_OFF = 'ON_OFF',
  RULES = 'RULES',
  REPLACE_RULES = 'REPLACE_RULES',
  FALLTHROUGH = 'FALLTHROUGH',
  OFF_VARIATION = 'OFF_VARIATION',
  EXPIRE_USER_TARGETS = 'EXPIRE_USER_TARGETS',
  EXPIRE_TARGETS = 'EXPIRE_TARGETS',
  MEMBERS_AND_TEAMS = 'MEMBERS_AND_TEAMS',
  VARIATIONS = 'VARIATIONS',
  MEASURED_ROLLOUT = 'MEASURED_ROLLOUT',
  PROGRESSIVE_ROLLOUT = 'PROGRESSIVE_ROLLOUT',
  AUTOMATED_RELEASE = 'AUTOMATED_RELEASE',
}

export enum ConflictKind {
  PENDING_CHANGES_WILL_FAIL = 'pendingChangeWillFail',
  PROPOSED_SCHEDULED_CHANGES_WILL_FAIL = 'proposedScheduledChangesWillFail',
  PROPOSED_APPROVED_CHANGES_WILL_FAIL = 'proposedApprovedChangesWillFail',
}

export const instructionKindToDescription = {
  [UserTargetsInstructionKind.ADD_USER_TARGETS]: 'Add user target',
  [UserTargetsInstructionKind.REMOVE_USER_TARGETS]: 'Remove user target',
  [UserTargetsInstructionKind.REPLACE_USER_TARGETS]: 'Replace individual users with',
  [TargetsInstructionKind.ADD_TARGETS]: 'Add target',
  [TargetsInstructionKind.REMOVE_TARGETS]: 'Remove target',
  [TargetsInstructionKind.REPLACE_TARGETS]: 'Replace targets with',
  [OnOffInstructionKind.TURN_FLAG_ON]: 'Update targeting to',
  [OnOffInstructionKind.TURN_FLAG_OFF]: 'Update targeting to',
  [OnOffInstructionKind.ENABLE_TARGETING]: 'Update targeting to',
  [OnOffInstructionKind.DISABLE_TARGETING]: 'Update targeting to',
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT]: 'Set default rule to',
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2]: 'Update with guarded rollout',
  [OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH]: 'Stop guarded rollout, set default rule to',
  [RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE]: 'Stop guarded rollout, set rule to',
  [RuleInstructionKind.ADD_RULE]: 'Add rule',
  [RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2]: 'Add rule with guarded rollout',
  [RuleInstructionKind.REMOVE_RULE]: 'Remove rule',
  [RuleInstructionKind.REORDER_RULES]: 'Reorder rules',
  [RuleInstructionKind.REPLACE_RULES]: 'Replace rules with',
  [ClauseInstructionKind.ADD_CLAUSES]: 'Add clause',
  [ClauseInstructionKind.REMOVE_CLAUSES]: 'Remove clause',
  [ClauseInstructionKind.UPDATE_CLAUSE]: 'Update clause',
  [ClauseInstructionKind.ADD_VALUES_TO_CLAUSE]: 'Add values to clause',
  [ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE]: 'Remove values from clause',
  [RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT]: 'Update rule variation to',
  [RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2]: 'Update with guarded rollout',
  [OnOffInstructionKind.UPDATE_OFF_VARIATION]: 'Update off variation to',
  [FlagPrerequisitesInstructionKind.ADD_PREREQUISITE]: 'Add prerequisite',
  [FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE]: 'Update prerequisite',
  [FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE]: 'Remove prerequisite',
  [FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES]: 'Replace prerequisites with',
  [RuleInstructionKind.UPDATE_RULE_DESCRIPTION]: 'Update rule description',
  [RuleInstructionKind.DISABLE_RULE]: 'Disable rule',
  [RuleInstructionKind.ENABLE_RULE]: 'Enable rule',
  [MembersAndTeamsInstructionKind.ADD_MEMBERS]: 'Add members',
  [MembersAndTeamsInstructionKind.REMOVE_MEMBERS]: 'Remove members',
  [MembersAndTeamsInstructionKind.ADD_TEAM_KEYS]: 'Add Team keys',
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE]: 'Add scheduled date',
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE]: 'Remove scheduled date',
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE]: 'Update scheduled date',
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE]: 'Add scheduled date',
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE]: 'Remove scheduled date',
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE]: 'Update scheduled date',
  [FlagVariationsInstructionKind.ADD_VARIATION]: 'Add variation',
  [FlagVariationsInstructionKind.REMOVE_VARIATION]: 'Remove variation',
  [FlagVariationsInstructionKind.UPDATE_VARIATION]: 'Update variation',
  [FlagVariationsInstructionKind.UPDATE_DEFAULT_VARIATION]: 'Change default',
  [AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE]: 'Stop automated release',
  [AutomatedReleaseInstructionKind.ADVANCE_AUTOMATED_RELEASE]: 'Advance phased release to the next phase',
  [RuleInstructionKind.START_AUTOMATED_RELEASE]: 'Start automated release',
};

export const InstructionKindToCategory = {
  [UserTargetsInstructionKind.ADD_USER_TARGETS]: InstructionCategory.USER_TARGETS,
  [UserTargetsInstructionKind.REMOVE_USER_TARGETS]: InstructionCategory.USER_TARGETS,
  [UserTargetsInstructionKind.REPLACE_USER_TARGETS]: InstructionCategory.REPLACE_USER_TARGETS,
  [TargetsInstructionKind.ADD_TARGETS]: InstructionCategory.TARGETS,
  [TargetsInstructionKind.REMOVE_TARGETS]: InstructionCategory.TARGETS,
  [TargetsInstructionKind.REPLACE_TARGETS]: InstructionCategory.REPLACE_TARGETS,
  [OnOffInstructionKind.TURN_FLAG_ON]: InstructionCategory.ON_OFF,
  [OnOffInstructionKind.TURN_FLAG_OFF]: InstructionCategory.ON_OFF,
  [OnOffInstructionKind.ENABLE_TARGETING]: InstructionCategory.ON_OFF,
  [OnOffInstructionKind.DISABLE_TARGETING]: InstructionCategory.ON_OFF,
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT]: InstructionCategory.FALLTHROUGH,
  [OnOffInstructionKind.STOP_MEASURED_ROLLOUT_ON_FALLTHROUGH]: InstructionCategory.FALLTHROUGH,
  [OnOffInstructionKind.UPDATE_FALLTHROUGH_WITH_MEASURED_ROLLOUT_V2]: InstructionCategory.FALLTHROUGH,
  [RuleInstructionKind.STOP_MEASURED_ROLLOUT_ON_RULE]: InstructionCategory.RULES,
  [RuleInstructionKind.START_AUTOMATED_RELEASE]: InstructionCategory.RULES,
  [RuleInstructionKind.ADD_RULE]: InstructionCategory.RULES,
  [RuleInstructionKind.ADD_RULE_WITH_MEASURED_ROLLOUT_V2]: InstructionCategory.RULES,
  [RuleInstructionKind.REMOVE_RULE]: InstructionCategory.RULES,
  [RuleInstructionKind.REORDER_RULES]: InstructionCategory.RULES,
  [RuleInstructionKind.REPLACE_RULES]: InstructionCategory.REPLACE_RULES,
  [ClauseInstructionKind.ADD_CLAUSES]: InstructionCategory.RULES,
  [ClauseInstructionKind.REMOVE_CLAUSES]: InstructionCategory.RULES,
  [ClauseInstructionKind.UPDATE_CLAUSE]: InstructionCategory.RULES,
  [ClauseInstructionKind.ADD_VALUES_TO_CLAUSE]: InstructionCategory.RULES,
  [ClauseInstructionKind.REMOVE_VALUES_FROM_CLAUSE]: InstructionCategory.RULES,
  [RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT]: InstructionCategory.RULES,
  [RuleInstructionKind.UPDATE_RULE_WITH_MEASURED_ROLLOUT_V2]: InstructionCategory.RULES,
  [OnOffInstructionKind.UPDATE_OFF_VARIATION]: InstructionCategory.OFF_VARIATION,
  [FlagPrerequisitesInstructionKind.ADD_PREREQUISITE]: InstructionCategory.PREREQUISITES,
  [FlagPrerequisitesInstructionKind.UPDATE_PREREQUISITE]: InstructionCategory.PREREQUISITES,
  [FlagPrerequisitesInstructionKind.REMOVE_PREREQUISITE]: InstructionCategory.PREREQUISITES,
  [FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES]: InstructionCategory.REPLACE_PREREQUISITES,
  [RuleInstructionKind.UPDATE_RULE_DESCRIPTION]: InstructionCategory.RULES,
  [RuleInstructionKind.DISABLE_RULE]: InstructionCategory.RULES,
  [RuleInstructionKind.ENABLE_RULE]: InstructionCategory.RULES,
  [MembersAndTeamsInstructionKind.ADD_MEMBERS]: InstructionCategory.MEMBERS_AND_TEAMS,
  [MembersAndTeamsInstructionKind.REMOVE_MEMBERS]: InstructionCategory.MEMBERS_AND_TEAMS,
  [MembersAndTeamsInstructionKind.ADD_TEAM_KEYS]: InstructionCategory.MEMBERS_AND_TEAMS,
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_USER_TARGET_DATE]: InstructionCategory.EXPIRE_USER_TARGETS,
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_USER_TARGET_DATE]: InstructionCategory.EXPIRE_USER_TARGETS,
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_USER_TARGET_DATE]: InstructionCategory.EXPIRE_USER_TARGETS,
  [ExpiringTargetsInstructionKind.ADD_EXPIRE_TARGET_DATE]: InstructionCategory.EXPIRE_TARGETS,
  [ExpiringTargetsInstructionKind.REMOVE_EXPIRE_TARGET_DATE]: InstructionCategory.EXPIRE_TARGETS,
  [ExpiringTargetsInstructionKind.UPDATE_EXPIRE_TARGET_DATE]: InstructionCategory.EXPIRE_TARGETS,
  [FlagVariationsInstructionKind.ADD_VARIATION]: InstructionCategory.VARIATIONS,
  [FlagVariationsInstructionKind.UPDATE_VARIATION]: InstructionCategory.VARIATIONS,
  [FlagVariationsInstructionKind.REMOVE_VARIATION]: InstructionCategory.VARIATIONS,
  [FlagVariationsInstructionKind.UPDATE_DEFAULT_VARIATION]: InstructionCategory.VARIATIONS,
  [AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE]: InstructionCategory.AUTOMATED_RELEASE,
  [AutomatedReleaseInstructionKind.ADVANCE_AUTOMATED_RELEASE]: InstructionCategory.AUTOMATED_RELEASE,
};
